import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppUser } from '../../../hooks/useAppUser';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import PublicIcon from '@mui/icons-material/Public';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useApi } from '../../../hooks/useApi';
import { keyframes } from '@mui/system';

dayjs.extend(duration);

// Pulsing animation for live indicator
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

// Format duration from start time to now
function formatDuration(startTime) {
  const diff = dayjs().diff(dayjs(startTime));
  const d = dayjs.duration(diff);
  const hours = Math.floor(d.asHours());
  const mins = d.minutes();
  const secs = d.seconds();

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Live duration component that updates every second
function LiveDuration({ startTime }) {
  const [durationStr, setDurationStr] = useState(() => formatDuration(startTime));
  const intervalRef = useRef(null);

  useEffect(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up new interval
    intervalRef.current = setInterval(() => {
      setDurationStr(formatDuration(startTime));
    }, 1000);

    // Cleanup on unmount or when startTime changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [startTime]);

  return (
    <Typography
      variant="body2"
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontWeight: 600,
        color: 'success.main',
      }}
    >
      {durationStr}+
    </Typography>
  );
}

// Participant display component - memoized to prevent re-renders
const ParticipantsList = memo(function ParticipantsList({ participants }) {
  if (!participants || participants.length === 0) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>—</Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {participants.map((p, idx) => {
        const name = p.odotId || 'Unknown';
        const location = [p.city, p.country].filter(Boolean).join(', ');

        return (
          <Box key={p.odotId || idx}>
            <Typography variant="body2" sx={{
              fontWeight: 600
            }}>{name}</Typography>
            {location && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                <PublicIcon sx={{ fontSize: 14 }} />
                {location}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
});

// Room row component - memoized to prevent re-renders when other rows change
const LiveRoomRow = memo(function LiveRoomRow({ room, onNavigate }) {
  // Memoize the click handler to avoid creating new function on every render
  const handleDoubleClick = useCallback(() => {
    onNavigate(`/rooms/${room.id}`);
  }, [onNavigate, room.id]);

  return (
    <TableRow
      hover
      onDoubleClick={handleDoubleClick}
      sx={{ cursor: 'pointer', verticalAlign: 'top' }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'success.main',
              animation: `${pulse} 2s infinite`,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" sx={{
            fontWeight: 500
          }}>{room.name}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2">{room.ownerEmail}</Typography>
          <Typography variant="body2" sx={{
            fontWeight: 600
          }}>{room.owner}</Typography>
          {(room.ownerCity || room.ownerCountry) && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
              <PublicIcon sx={{ fontSize: 14 }} />
              {[room.ownerCity, room.ownerCountry].filter(Boolean).join(', ')}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <ParticipantsList participants={room.participants} />
      </TableCell>
      <TableCell>
        <Chip
          label={room.region || 'unknown'}
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{dayjs(room.timeStarted).format('h:mm A')}</Typography>
      </TableCell>
      <TableCell>
        <LiveDuration startTime={room.timeStarted} />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {room.recordings?.map((rec) => (
            <Box key={rec.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
                label="REC"
                size="small"
                color="error"
              />
              <Typography variant="body2">{dayjs(rec.timeStarted).format('h:mm A')}</Typography>
              <LiveDuration startTime={rec.timeStarted} />
            </Box>
          ))}
          {room.streamings?.map((stream) => (
            <Box key={stream.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={<LiveTvIcon sx={{ fontSize: 14 }} />}
                label="LIVE"
                size="small"
                color="primary"
              />
              <Typography variant="body2">{dayjs(stream.timeStarted).format('h:mm A')}</Typography>
              <LiveDuration startTime={stream.timeStarted} />
            </Box>
          ))}
          {room.transcriptions?.map((trans) => (
            <Box key={trans.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={<SubtitlesIcon sx={{ fontSize: 14 }} />}
                label="CC"
                size="small"
                color="secondary"
              />
              <Typography variant="body2">{dayjs(trans.timeStarted).format('h:mm A')}</Typography>
              <LiveDuration startTime={trans.timeStarted} />
            </Box>
          ))}
          {!room.recordings?.length && !room.streamings?.length && !room.transcriptions?.length && (
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>—</Typography>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
});

function LiveRooms() {
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useAppUser();

  // Memoize navigate callback to prevent all LiveRoomRow components from re-rendering
  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const userId = user?.id || user?._id || user?.email;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['rooms', 'live', userId],
    queryFn: () => api.get('/api/rooms/live'),
    enabled: !!userId,
    refetchInterval: 10000,
    placeholderData: (previousData) => previousData,
  });

  const rooms = data?.rooms || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">Live Rooms</Typography>
        {isFetching && !isLoading && <CircularProgress size={20} />}
        <Chip
          label={`${rooms.length} active`}
          color={rooms.length > 0 ? 'success' : 'default'}
          size="small"
        />
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            ml: 'auto'
          }}>
          Auto-refreshes every 10 seconds
        </Typography>
      </Box>
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Room</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Participants</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rooms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>No live rooms</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rooms.map((room) => (
                  <LiveRoomRow key={room.id} room={room} onNavigate={handleNavigate} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default LiveRooms;
